import type { AxiosInstance } from 'axios'

import { logger } from '@/utils/logger'

/**
 * Delivery service — one service per backend resource, and the only place
 * that calls the HTTP client (React_TypeScript_Standards.md §4).
 *
 * There is no `/api/delivery` endpoint yet, so the exported instance is
 * fixture-backed. The point of routing through here anyway is that the page
 * already talks to the shape it will keep: when the API lands, only the
 * `deliveryService` line at the bottom of this file changes.
 */

/**
 * Where the delivery is in the process. One value, stored, moved only by
 * events. ETA deliberately claims only what we have evidence for -- a time --
 * and nothing about the vehicle (the carrier-industry pattern: FedEx says
 * "shipment information sent", never a guess). REFUSED is the carrier word
 * for the *event* of turning a truck away; the QC verdict on the material is
 * the cargo axis's REJECTED.
 */
export type DeliveryStage =
  'ETA' | 'On site' | 'Unloading' | 'Received' | 'Refused' | 'Cancelled'

/**
 * What we decided about the load -- the Receiving page's line-level
 * accept/reject rolled up to one value. Born when the delivery reaches
 * On site; deliveries still inbound have none. PARTIAL is deliberately
 * coarse (defects and short-ships alike); the lines carry the detail.
 */
export type CargoDisposition =
  'Pending' | 'Verifying' | 'Full' | 'Partial' | 'Rejected'

const TERMINAL_STAGES: ReadonlySet<DeliveryStage> = new Set<DeliveryStage>([
  'Received',
  'Refused',
  'Cancelled',
])

/** Done with, one way or the other — nothing left to reschedule. */
export function isTerminalStage(stage: DeliveryStage): boolean {
  return TERMINAL_STAGES.has(stage)
}

/**
 * Still expected -- the only state that can be overdue or late, and the only
 * one where rescheduling means anything. Once a truck is on site it is
 * standing in front of someone, whatever the clock says about its slot.
 */
export function isAwaitingArrival(stage: DeliveryStage): boolean {
  return stage === 'ETA'
}

/**
 * Display text for a delivery's carrier -- a load may be arranged before the
 * carrier is known, so `carrier` can be empty. Shared by the tile heading and
 * the add-delivery toast so the two can't disagree on what an unassigned
 * carrier looks like.
 */
export function carrierLabel(carrier: string): string {
  return carrier || 'Carrier TBD'
}

/**
 * A scheduled delivery.
 *
 * Carries a material *description* but no line items or counts: a delivery is
 * usually a manifest, and enumerating it here would misrepresent that. The
 * packing list belongs to Receiving (story 121557). The description exists so
 * an SIS can tell rebar from modules and prep the yard accordingly.
 */
export type DeliveryDto = {
  id: string
  /**
   * Who is hauling the load — the name painted on the truck coming down the
   * road. The tile's heading, because matching cab to row is how the board
   * gets scanned.
   */
  carrier: string
  /** Who sold the material. Equals the carrier on self-hauled loads. */
  vendor: string
  /** Summary of what is on the truck. A description, not a manifest. */
  material: string
  /** Drop-off point within the project — no project prefix; the selected
      project is already named in the top bar. */
  destination: string
  /** Local ISO `YYYY-MM-DDTHH:MM`. See utils/scheduleTime.ts. */
  scheduledFor: string
  /**
   * When the delivery was *committed* to arrive -- the promise, where
   * `scheduledFor` is the working ETA every reschedule updates. Never
   * touched by the popover. Nothing on the board derives from it any more
   * (the Late tag keys off the working ETA alone); kept as the first sliver
   * of the audit trail. Provisional -- QUESTIONS.md Q10.
   */
  promisedFor?: string
  driver: string
  stage: DeliveryStage
  /**
   * Where the record came from: the extraction pipeline (propose -> human
   * confirms; the modal shows its provenance line and source-message link)
   * or manual entry at the site.
   */
  source: 'extracted' | 'manual'
  /** Absent until the delivery reaches On site -- inbound rows have no
      cargo story yet, and absence is the display. */
  cargoDisposition?: CargoDisposition
  note?: string
  /** Why the office cancelled this delivery -- kept separate from `note`
      (the yard's freeform note) because it answers a different question and
      later has to be shown on its own (the cancel dialog, an eventual
      transfer's audit trail). Absent on a delivery that was never cancelled. */
  statusNote?: string
  /** Whether the office has phoned the carrier to confirm the load is
      actually coming — a manual check separate from the extraction
      pipeline's confidence in the record itself. Drives the tile's
      calendar-check glyph. */
  callConfirmed?: boolean

  /* ---------- Paperwork references ----------
     All optional, and their absence is meaningful rather than a data gap. */

  /**
   * Bill of lading — the carrier's document, what the driver hands over.
   * Absent until the BOL email lands and the extraction pipeline reads it,
   * so future deliveries known only from BOM/PO data have none.
   */
  bolNumber?: string
  /**
   * Blattner purchase or sales order, `####-########-###` — project, order,
   * line. Required: a delivery always fulfils some PO, even when no paperwork
   * prints it (~43% of BOLs arrive PO-less — that is a matching problem, not
   * a data one; working answer to Q1, revisit if receiving *unexpected*
   * deliveries ever becomes a feature). The BOL survey found printed POs
   * typo-prone ('146-' for '1146-'), which is why matching needs fuzzy repair.
   */
  bpoNumber: string
  /**
   * The supplier's own order / pack-list number. The BOL survey's only
   * identity field marked *always present* when a document exists, which
   * makes it the strongest key for matching paperwork to a row. Format
   * varies by the supplier's ERP (SAP, Plex, Axis, Oracle).
   */
  supplierOrderNumber?: string
  /**
   * Gross weight in pounds. Universal on every document surveyed, so it is
   * a dependable sanity check at the gate even though it identifies nothing
   * on its own. LB because every surveyed document used it.
   */
  grossWeightLb?: number
  /**
   * `YYYY-MM-DD` the load left the vendor. Absent means not yet shipped —
   * which read against the scheduled arrival is the useful signal: shipped
   * three days ago and still not here is a very different problem.
   */
  shipDate?: string
}

/**
 * The fields the View/Edit modal may change. Identity (vendor, PO) and
 * provenance are not among them — they are the record's anchor.
 *
 * TODO: bolNumber/note use `undefined` to mean "clear this field," which
 * works today because everything stays in memory -- but JSON.stringify
 * drops an `undefined` key entirely, so the real PATCH below will silently
 * not transmit the clear once the endpoint exists. Decide a `null`-to-clear
 * convention on this type (and update DeliveryEditDialog's handleSubmit,
 * which is where `undefined` gets produced) before wiring up /api/delivery.
 */
export type DeliveryUpdate = Partial<
  Pick<
    DeliveryDto,
    | 'carrier'
    | 'scheduledFor'
    | 'bolNumber'
    | 'material'
    | 'driver'
    | 'destination'
    | 'note'
    | 'stage'
    | 'callConfirmed'
    | 'statusNote'
  >
>

/**
 * A hand-entered delivery, as the Add form submits it. Everything the SIS
 * types is text -- vendor, carrier, PO included -- because that is what the
 * schema stores (`SupplierName`, `Carrier`, `PoReferenceAsPrinted`); lookups
 * arrive later as optional ids *beside* these fields, never in place of them.
 * The service assigns id, stage (ETA) and provenance (manual).
 */
export type DeliveryCreate = Pick<
  DeliveryDto,
  | 'vendor'
  | 'carrier'
  | 'material'
  | 'destination'
  | 'scheduledFor'
  | 'driver'
  | 'bpoNumber'
> &
  Partial<
    Pick<
      DeliveryDto,
      'supplierOrderNumber' | 'bolNumber' | 'note' | 'callConfirmed'
    >
  >

export type DeliveryService = {
  /** Scheduled deliveries for the current window, unordered — the view sorts. */
  getScheduled: (signal?: AbortSignal) => Promise<DeliveryDto[]>
  /**
   * Record a delivery the site arranged itself. Resolves with the row as the
   * service now holds it -- the id is the service's to assign, so the board
   * waits for this rather than inventing one.
   */
  create: (input: DeliveryCreate, signal?: AbortSignal) => Promise<DeliveryDto>
  /**
   * Move a delivery to a new time. `scheduledFor` is the full local ISO
   * value, not a delta — the caller has already worked out the result, and
   * sending an absolute time keeps the write idempotent on retry.
   */
  reschedule: (
    id: string,
    scheduledFor: string,
    signal?: AbortSignal,
  ) => Promise<void>
  /** Apply a View/Edit save. Absolute values, not deltas, for idempotency. */
  update: (
    id: string,
    changes: DeliveryUpdate,
    signal?: AbortSignal,
  ) => Promise<void>
}

/** The real implementation, ready for the endpoints that do not exist yet. */
export function createDeliveryService(client: AxiosInstance): DeliveryService {
  return {
    getScheduled: async (signal) => {
      const response = await client.get<DeliveryDto[]>('/api/delivery', {
        signal,
      })
      return response.data
    },
    create: async (input, signal) => {
      const response = await client.post<DeliveryDto>('/api/delivery', input, {
        signal,
      })
      return response.data
    },
    reschedule: async (id, scheduledFor, signal) => {
      await client.patch(
        `/api/delivery/${encodeURIComponent(id)}`,
        { scheduledFor },
        { signal },
      )
    },
    update: async (id, changes, signal) => {
      await client.patch(`/api/delivery/${encodeURIComponent(id)}`, changes, {
        signal,
      })
    },
  }
}

/**
 * Optional local sample data. `src/mock-data/` is gitignored, so it is
 * present on a developer's machine and absent in a fresh clone and in CI.
 *
 * import.meta.glob rather than a plain import on purpose: a glob that matches
 * nothing resolves to an empty object, where a missing import is a build
 * error. That is what lets the folder be ignored without breaking anyone.
 */
const log = logger.scoped('deliveryService')

type LocalFixture = {
  buildMockDeliveries: (anchor?: Date) => DeliveryDto[]
}

const localFixtures = import.meta.glob<LocalFixture>(
  '../mock-data/deliveries.ts',
  { eager: true },
)

/**
 * Stand-in until the endpoint exists. Serves local sample data when the
 * developer has some, and an empty schedule when they do not — which renders
 * the board's empty state rather than failing.
 *
 * Stateful for the life of the page load: writes land in an in-memory board
 * so a delivery added or rescheduled survives navigating away and back, the
 * way it will against a real endpoint. A reload starts over from the fixture.
 */
export function createMockDeliveryService(): DeliveryService {
  let board: DeliveryDto[] | null = null

  function loadBoard(): DeliveryDto[] {
    if (board === null) {
      const fixture = Object.values(localFixtures)[0]
      board = fixture ? fixture.buildMockDeliveries() : []
    }
    return board
  }

  /** `DLV-` plus one past the highest number on the board -- the same shape
      the fixture uses, so nothing downstream can tell a hand-entered row
      apart by its id. */
  function nextId(rows: DeliveryDto[]): string {
    const highest = rows.reduce((max, row) => {
      const n = Number(row.id.replace(/^DLV-/, ''))
      return Number.isFinite(n) && n > max ? n : max
    }, 1000)
    return `DLV-${highest + 1}`
  }

  return {
    getScheduled: async () => [...loadBoard()],
    create: async (input) => {
      const rows = loadBoard()
      const created: DeliveryDto = {
        ...input,
        id: nextId(rows),
        stage: 'ETA',
        source: 'manual',
        promisedFor: input.scheduledFor,
      }
      rows.push(created)
      log.info('Create (in memory — no endpoint yet)', { id: created.id })
      return created
    },
    reschedule: async (id, scheduledFor) => {
      const row = loadBoard().find((delivery) => delivery.id === id)
      if (row) row.scheduledFor = scheduledFor
      log.info('Reschedule (in memory — no endpoint yet)', { id, scheduledFor })
    },
    update: async (id, changes) => {
      const rows = loadBoard()
      const index = rows.findIndex((delivery) => delivery.id === id)
      const row = rows[index]
      if (row) rows[index] = { ...row, ...changes }
      log.info('Update (in memory — no endpoint yet)', { id, changes })
    },
  }
}

// Swap for `createDeliveryService(apiClient)` when /api/delivery exists.
// Nothing outside this file should need to change.
export const deliveryService: DeliveryService = createMockDeliveryService()
